import { Dispatch, SetStateAction, useState } from 'react'
import { useParams } from 'react-router'
import { Button, Stack } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useDocumentDashboardIds from '@/hooks/useDocumentDashboardIds'
import useOverlay from '@/hooks/useOverlay'
import { Workflow, WorkflowState } from '@/types/workflows'
import { MAX_FILTERED_EXPORT_DOCUMENTS } from '@/utils/document-export'
import { useDocumentFiltersContext } from '@/components/document-filters/DocumentFiltersProvider'
import DashboardEventButton from './DashboardEventButton'
import ExportDocumentsDialog from './ExportDocumentsDialog'

type DocumentGridActionButtonsProps = {
  listQueryKey: QueryKey
  queryBy: 'rows' | 'documents'
  workflow: Workflow
  state?: WorkflowState
  selectedDocumentIds: string[]
  tableHasData: boolean
  filteredDocumentsCount: number
  setSelectedRows: Dispatch<SetStateAction<Record<string, boolean>>>
  onViewSelected: () => void
}

export default function DocumentGridActionButtons({
  workflow,
  state,
  selectedDocumentIds,
  tableHasData,
  filteredDocumentsCount,
  setSelectedRows,
  onViewSelected,
  ...rest
}: DocumentGridActionButtonsProps) {
  const exportOverlay = useOverlay()
  const { projectId } = useParams()
  const [exportAll, setExportAll] = useState(true)
  const { isApplyingFilters, tableCuratedFilters } = useDocumentFiltersContext()

  const filteredExportAll = exportAll && isApplyingFilters
  const {
    documents: filteredDocumentIds,
    isFetchingAll,
    isFetching,
    isError,
  } = useDocumentDashboardIds({
    ...tableCuratedFilters,
    enabled:
      exportOverlay.isOpen &&
      filteredExportAll &&
      filteredDocumentsCount <= MAX_FILTERED_EXPORT_DOCUMENTS,
  })

  const exceedsExportLimit =
    filteredExportAll &&
    (filteredDocumentsCount > MAX_FILTERED_EXPORT_DOCUMENTS ||
      filteredDocumentIds.length > MAX_FILTERED_EXPORT_DOCUMENTS)
  const documentIdsLookupFailed = isError && !isFetching
  const isResolvingDocumentIds =
    filteredExportAll &&
    !exceedsExportLimit &&
    !documentIdsLookupFailed &&
    (isFetchingAll || isFetching)
  const hasDocumentIdsError =
    filteredExportAll &&
    !exceedsExportLimit &&
    !isResolvingDocumentIds &&
    (isError || filteredDocumentIds.length === 0)

  let documentIds: string[] | undefined = selectedDocumentIds
  if (exportAll) {
    documentIds = isApplyingFilters
      ? filteredDocumentIds.map(({ id }) => id)
      : undefined
  }

  return (
    <Stack
      data-testid="dg-action-buttons"
      direction="row"
      spacing={1}
      flexGrow={1}
      justifyContent="flex-end"
    >
      <Button
        disabled={!selectedDocumentIds.length}
        variant="text"
        size="small"
        onClick={() => {
          exportOverlay.open()
          setExportAll(false)
        }}
      >
        Export Selected ({selectedDocumentIds.length})
      </Button>
      <Button
        disabled={!tableHasData}
        variant="text"
        size="small"
        onClick={() => {
          exportOverlay.open()
          setExportAll(true)
        }}
      >
        {isApplyingFilters
          ? `Export All (${filteredDocumentsCount})`
          : 'Export All'}
      </Button>
      <ExportDocumentsDialog
        overlay={exportOverlay}
        projectId={projectId as string}
        state={state}
        workflow={workflow}
        setSelectedRows={setSelectedRows}
        documentIds={documentIds}
        exceedsExportLimit={exceedsExportLimit}
        isResolvingDocumentIds={isResolvingDocumentIds}
        hasDocumentIdsError={hasDocumentIdsError}
      />
      <Button
        disabled={!selectedDocumentIds.length}
        size="small"
        variant="text"
        onClick={onViewSelected}
      >
        View Selected ({selectedDocumentIds.length})
      </Button>

      {state && (
        <DashboardEventButton
          documentIds={selectedDocumentIds}
          workflowStateId={state.id}
          {...rest}
        />
      )}
    </Stack>
  )
}
